import React, { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { toast } from "sonner"
import { authAPI, getErrorMessage } from "../../utils/api"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Mail } from "lucide-react"

const ForgotPassword = () => {
  const navigate = useNavigate()
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await authAPI.requestPasswordReset({ email })
      toast.success("If an account exists, we sent a reset link to your email.")
      navigate("/login")
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to send reset link"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center h-full bg-[#060638]">
      <div className="w-full overflow-hidden flex flex-col md:flex-row min-h-[80vh] md:h-[90vh] my-auto gap-6 md:gap-10 bg-transparent px-4 md:px-6 pt-4 md:pt-0">
        {/* Left Panel - Image */}
        <div className="w-full md:w-1/2 flex items-center justify-center relative overflow-hidden rounded-2xl min-h-[240px] sm:min-h-[320px] md:min-h-0 md:h-full shrink-0 p-4 md:p-0">
          <img
            src="/login.png"
            alt=""
            className="w-full h-auto object-contain rounded-2xl md:h-full md:w-full"
          />
        </div>

        {/* Right Panel - Form */}
        <div className="w-full md:w-1/2 p-4 md:p-8 lg:p-10 xl:p-12 flex flex-col justify-start">
          <div className="flex items-center justify-between w-full max-w-[630px] mb-6 md:mb-10 lg:mb-12">
            <img src="/images/logo.png" alt="Logo" className="h-8 md:h-10 lg:h-12 mr-auto" />
            <Link
              to="/register"
              className="text-white hover:underline items-center font-medium border border-white rounded-full px-3 md:px-4 py-1.5 md:py-2 justify-center text-sm md:text-base"
              data-testid="register-link"
            >
              Sign Up
            </Link>
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-[44px] font-bold text-white mb-6 md:mb-8 lg:mb-10">
            <span className="text-white">Forgot Password</span>
          </h1>

          <p className="text-gray-200 mb-6 md:mb-8 text-sm md:text-base">
            Enter the email associated with your account and we'll send you a
            secure link to reset your password.
          </p>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5 w-full max-w-[630px]"
            data-testid="forgot-password-form"
          >
            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 !bg-white border-0 text-[16px] rounded-none pl-12 pr-4 !text-gray-900 placeholder:text-gray-400 placeholder:text-[16px] focus:ring-2 focus:ring-green-500 focus:outline-none shadow-sm"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-14 bg-[#ff2d2d] hover:bg-[#7f1d1d] text-white font-semibold rounded-none shadow-xl flex items-center justify-center gap-2 transition-all duration-300 transition-ease-in-out mt-5"
              disabled={loading}
              data-testid="forgot-password-submit"
            >
              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                <>
                  <span>Send Reset Link</span>
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-left space-y-2">
            <p className="text-gray-200 text-sm md:text-base">
              Remembered your password?{" "}
              <Link
                to="/login"
                className="text-gray-200 hover:text-white hover:underline font-medium"
              >
                Back to Sign In
              </Link>
            </p>
            <p className="text-gray-200 text-sm md:text-base">
              New to Aqua?{" "}
              <Link
                to="/register"
                className="text-gray-200 hover:text-white hover:underline font-medium"
              >
                Create Account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword
